import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { decodeTime } from 'ulid';

import { isJson } from 'tools/helpers';

const GET_TASK_RUN = gql`
  query getTaskRun($taskRunId: String!, $taskYYMM: String!) {
    getToolDetail(taskRunId: $taskRunId, taskYYMM: $taskYYMM) {
      status
      data {
        type
        result
      }
    }
  }
`;

const POLL_INTERVAL_MS = 2000;

/**
 * How long a run can stay unfinished before we stop waiting on it: past the 900s timeout of the
 * longest tool Lambda, so a record still IN_PROGRESS after this was killed or never finished
 * (withTools only writes a final status when the handler returns or throws).
 */
export const TASK_RUN_MAX_WAIT_MS = 20 * 60 * 1000;

/**
 * The run record's partition key, as withTools writes it: the UTC month the run started in. The
 * taskRunId is a ULID minted at submit, so it carries that time.
 */
export const getTaskYYMM = (taskName, taskRunId) => {
  const startedAt = new Date(decodeTime(taskRunId));
  return `${taskName}-${startedAt.getUTCFullYear()}-${startedAt.getUTCMonth() + 1}`;
};

const toolError = (error) => ({ __typename: 'ToolError', success: false, error });

/**
 * For a tool whose resolver returns before the run ends: polls the run record until withTools marks
 * it finished, then returns the tool's result in the same shape as a synchronous mutation response.
 * Returns undefined while the run is still going, and an error if it can't be checked or never finishes.
 */
export const useTaskRunResult = ({ taskName, taskRunId }) => {
  const [pollInterval, setPollInterval] = useState(POLL_INTERVAL_MS);
  const [timedOutTaskRunId, setTimedOutTaskRunId] = useState();
  const { data, error } = useQuery(GET_TASK_RUN, {
    variables: { taskRunId, taskYYMM: taskRunId ? getTaskYYMM(taskName, taskRunId) : undefined },
    skip: !taskRunId,
    pollInterval,
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!taskRunId) {
      return undefined;
    }
    const waitLeftMs = decodeTime(taskRunId) + TASK_RUN_MAX_WAIT_MS - Date.now();
    const timer = setTimeout(() => setTimedOutTaskRunId(taskRunId), Math.max(0, waitLeftMs));
    return () => clearTimeout(timer);
  }, [taskRunId]);

  const run = data?.getToolDetail;
  const finished = !!run?.status && run.status !== 'IN_PROGRESS';
  const timedOut = !finished && !!taskRunId && timedOutTaskRunId === taskRunId;

  // Driven by the option, not stopPolling(): a stopped poll stays stopped when the next submit
  // changes taskRunId, so that run would never be polled.
  const nextPollInterval = finished || timedOut ? 0 : POLL_INTERVAL_MS;
  if (pollInterval !== nextPollInterval) {
    setPollInterval(nextPollInterval);
  }

  return useMemo(() => {
    if (timedOut) {
      return toolError(
        `This run hasn't finished after ${TASK_RUN_MAX_WAIT_MS / 60000} minutes. Check Recent Runs for its status.`
      );
    }
    if (!finished) {
      // Polling continues, so a one-off failure clears on the next successful check
      return error
        ? toolError(`Couldn't check on this run: ${error.message}. Check Recent Runs for the result.`)
        : undefined;
    }
    const output = run.data?.find((item) => item.type === 'OUTPUT');
    const result = isJson(output?.result) ? JSON.parse(output.result) : output?.result;
    // A run that threw stores no result
    return result ?? toolError('The run failed before it returned a result. Open it under Recent Runs for details.');
  }, [timedOut, finished, run, error]);
};
